import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success("Welcome to Youngness Clinic Portal");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Left visual */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1762625570087-6d98fca29531?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxhZXN0aGV0aWMlMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODYzNjg3OTd8MA&ixlib=rb-4.1.0&q=85" alt="clinic" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-blue-800/70 to-teal-800/70" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center font-heading font-bold text-xl">Y</div>
          <span className="font-heading font-extrabold text-xl">Youngness Clinic</span>
        </div>
        <div className="relative z-10 space-y-4">
          <h2 className="font-heading text-4xl font-extrabold tracking-tighter leading-tight">The single source of truth for procedure pricing & commissions.</h2>
          <p className="text-white/80 max-w-md">Manage aesthetic, cosmetic and surgical procedure pricing, packages, commissions and audit history in one premium dashboard.</p>
        </div>
        <div className="relative z-10 text-white/60 text-sm">© {new Date().getFullYear()} Youngness Clinic by Dr. Awish</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
            <p className="text-slate-500 mt-1">{mode === "login" ? "Sign in to manage clinic pricing." : "Staff accounts have read-only access."}</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Doe" className="h-11" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@clinic.com" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" className="h-11" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            {mode === "login" ? (
              <>New staff member?{" "}
                <button className="text-blue-600 font-medium" onClick={() => { setMode("register"); setError(""); }} data-testid="switch-register">Register</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button className="text-blue-600 font-medium" onClick={() => { setMode("login"); setError(""); }} data-testid="switch-login">Sign in</button>
              </>
            )}
          </div>

          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
            <div className="flex items-center gap-2 font-medium text-slate-800 mb-1"><ShieldCheck className="h-4 w-4 text-teal-600" /> Admin demo</div>
            abhipa2@gmail.com &nbsp;/&nbsp; Admin@123
          </div>
        </motion.div>
      </div>
    </div>
  );
}
